import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../i18n";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="mb-3 font-sans text-xl font-bold text-ink sm:text-2xl">{title}</h2>
    <div className="space-y-3 font-body text-base leading-relaxed text-ink/70">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  const { lang } = useLang();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = lang === "it" ? "Privacy Policy | BOXLY" : "Privacy Policy | BOXLY";
  }, [lang]);

  const it = lang === "it";
  return (
    <main className="bg-white pb-24 pt-28 sm:pt-32" data-testid="privacy-page">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-ink/60 transition-colors hover:text-electric-blue" data-testid="back-home">
          <ArrowLeft className="h-3.5 w-3.5" /> {it ? "Torna alla home" : "Back to home"}
        </Link>
        <h1 className="mt-6 font-display text-4xl font-black leading-[0.95] text-ink sm:text-5xl">
          {it ? "Privacy Policy" : "Privacy Policy"}
        </h1>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-ink/50">
          {it ? "Ultimo aggiornamento" : "Last updated"}: 12/2026
        </p>

        <div className="mt-12">
          <Section title={it ? "Titolare del trattamento" : "Data controller"}>
            <p>
              <strong>NEWNETWORK S.R.L.S.</strong> — {it ? "società titolare del brand" : "company owning the brand"} BOXLY.
            </p>
            <p>
              {it ? "Sede legale:" : "Registered office:"} Corso San Giovanni a Teduccio 440, 80146 Napoli (NA), Italia.<br />
              P.IVA / C.F.: 10290721215 · REA NA-1094905 · {it ? "Capitale sociale" : "Share capital"} € 2.500,00 i.v.
            </p>
            <p>PEC: <a href="mailto:newnetworksrls@pec.it" className="text-electric-blue underline">newnetworksrls@pec.it</a></p>
            <p>{it ? "Email di contatto per la privacy:" : "Privacy contact email:"} <a href="mailto:info@boxlyitalia.it" className="text-electric-blue underline">info@boxlyitalia.it</a></p>
          </Section>

          <Section title={it ? "Dati raccolti dal sito" : "Data collected by the site"}>
            <p>{it
              ? "Il sito raccoglie dati esclusivamente quando l'utente compila il modulo di contatto o interagisce volontariamente con le sezioni del sito. In aggiunta, alcuni servizi tecnici possono registrare dati di navigazione (log tecnici, indirizzo IP, tipo di browser) per il funzionamento e la sicurezza."
              : "The site collects data only when the user fills in the contact form or voluntarily interacts with the site. In addition, some technical services may log navigation data (technical logs, IP address, browser type) for operation and security."}
            </p>
          </Section>

          <Section title={it ? "Dati inseriti nel form" : "Data submitted via the form"}>
            <p>{it
              ? "Nome e cognome, azienda o struttura di riferimento, indirizzo email, telefono (facoltativo), tipologia di struttura, numero indicativo di unità (facoltativo), messaggio libero e conferma del consenso privacy."
              : "Full name, company or building of reference, email address, phone (optional), building type, approximate number of units (optional), free-text message and privacy consent confirmation."}</p>
          </Section>

          <Section title={it ? "Finalità del trattamento" : "Purposes of processing"}>
            <p>{it ? "I dati vengono trattati per:" : "Data is processed to:"}</p>
            <ul className="ml-5 list-disc space-y-1">
              <li>{it ? "rispondere alle richieste inviate tramite il form;" : "reply to inquiries submitted via the form;"}</li>
              <li>{it ? "valutare candidature per il programma pilota Boxly;" : "evaluate applications for the Boxly pilot program;"}</li>
              <li>{it ? "adempiere a obblighi di legge e garantire la sicurezza del sito." : "comply with legal obligations and ensure site security."}</li>
            </ul>
          </Section>

          <Section title={it ? "Base giuridica" : "Legal basis"}>
            <p>{it
              ? "Esecuzione di misure precontrattuali adottate su richiesta dell'interessato (art. 6.1.b GDPR) e legittimo interesse del titolare a rispondere alle richieste e garantire la sicurezza tecnica del sito (art. 6.1.f GDPR). Non sono previsti trattamenti basati su consenso in questa fase."
              : "Performance of pre-contractual measures taken at the data subject's request (art. 6.1.b GDPR) and legitimate interest of the controller to reply to inquiries and ensure technical site security (art. 6.1.f GDPR). No consent-based processing is performed at this stage."}</p>
          </Section>

          <Section title={it ? "Servizi tecnici utilizzati" : "Technical services used"}>
            <ul className="ml-5 list-disc space-y-1">
              <li>{it ? "Hosting e infrastruttura cloud per l'erogazione del sito." : "Hosting and cloud infrastructure for site delivery."}</li>
              <li>{it ? "Servizio di invio email per la ricezione delle richieste (i dati del form vengono trasmessi via API a un provider di email transazionale)." : "Email delivery service for form submissions (form data is sent via API to a transactional email provider)."}</li>
              <li>{it ? "Font hosting via Fontshare (CDN esterno di caratteri tipografici)." : "Font hosting via Fontshare (external font CDN)."}</li>
              <li>{it ? "Strumenti tecnici di analisi/registrazione sessione utilizzati dalla piattaforma di hosting per fini diagnostici — vedi Cookie Policy." : "Technical analytics/session recording tools used by the hosting platform for diagnostic purposes — see Cookie Policy."}</li>
            </ul>
          </Section>

          <Section title={it ? "Tempi di conservazione" : "Retention periods"}>
            <p>{it
              ? "I dati inviati tramite il form vengono conservati per il tempo necessario a gestire la richiesta e successivi contatti commerciali, e comunque non oltre 24 mesi salvo obblighi di legge o consenso specifico per usi ulteriori."
              : "Data submitted via the form is kept for the time needed to handle the request and any subsequent commercial contact, and in any case no longer than 24 months unless required by law or specific further consent."}</p>
          </Section>

          <Section title={it ? "Diritti dell'interessato" : "Rights of the data subject"}>
            <p>{it
              ? "L'interessato può in ogni momento esercitare i diritti previsti dagli artt. 15-22 GDPR: accesso, rettifica, cancellazione, limitazione, opposizione, portabilità, revoca del consenso e reclamo all'autorità di controllo."
              : "The data subject may at any time exercise the rights under arts. 15-22 GDPR: access, rectification, erasure, restriction, objection, portability, withdrawal of consent and complaint to the supervisory authority."}</p>
          </Section>

          <Section title={it ? "Come contattarci" : "How to contact us"}>
            <p>{it
              ? "Per esercitare i propri diritti o per qualsiasi richiesta relativa al trattamento dei dati, è possibile scrivere a "
              : "To exercise your rights or for any request regarding data processing, please write to "}
              <a href="mailto:info@boxlyitalia.it" className="text-electric-blue underline">info@boxlyitalia.it</a>.
            </p>
          </Section>
        </div>
      </div>
    </main>
  );
}
